import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert Google Drive URLs to reliable thumbnail URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return '/placeholder.jpg';
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w800`;
  
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w800`;
  
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w800`;
  
  return url;
};

// Collapsible News Component
const CollapsibleNews = ({ title, items, convertUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="mt-12 border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        <ChevronDown 
          size={18} 
          className={`text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {isOpen && (
        <div className="p-4 border-t border-border">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.slice(0, 6).map((item) => (
              <Link 
                key={item.content_id}
                to={`/${item.slug}`}
                className="block group"
                data-testid={`content-${item.slug}`}
              >
                <div className="relative aspect-[16/9] bg-black overflow-hidden mb-3">
                  <img
                    src={convertUrl(item.image)}
                    alt={item.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                  />
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-widest mb-1">
                  <span>{item.category || item.type}</span>
                  {item.source && (
                    <>
                      <span>·</span>
                      <span>{item.source}</span>
                    </>
                  )}
                </div>
                <h4 className="text-sm editorial-serif group-hover:accent-red transition-colors line-clamp-2">
                  {item.title}
                </h4>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ArtistsPage = () => {
  const [artists, setArtists] = useState([]);
  const [content, setContent] = useState([]);
  const [brandSites, setBrandSites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [artistsRes, contentRes, brandSitesRes] = await Promise.all([
          fetch(`${API_URL}/api/artists`),
          fetch(`${API_URL}/api/content`),
          fetch(`${API_URL}/api/brand-sites/public`).catch(() => null)
        ]);
        
        if (artistsRes.ok) {
          const data = await artistsRes.json();
          setArtists(data);
        }
        if (contentRes.ok) {
          const data = await contentRes.json();
          setContent(data);
        }
        if (brandSitesRes?.ok) {
          const data = await brandSitesRes.json();
          setBrandSites(data);
        }
      } catch (error) {
        console.error('Error fetching artists:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get content items related to a specific artist (including from brand sites)
  const getArtistContent = (artistName, artistId) => {
    // Direct content matches
    const directContent = content.filter(item => {
      if (item.artist && item.artist.toLowerCase().includes(artistName.toLowerCase())) {
        return true;
      }
      return false;
    });
    
    // Find brand sites that link to this artist and get their content
    const linkedBrandSiteContent = [];
    brandSites.forEach(site => {
      const isLinked = (site.sections || []).some(section => 
        (section.linked_artists || []).some(a => 
          a.artist_id === artistId || a.name?.toLowerCase() === artistName.toLowerCase()
        )
      );
      
      if (isLinked) {
        // Add site-scoped content that isn't already in the list
        const siteContent = content.filter(item => 
          item.site === site.slug && 
          !directContent.some(dc => dc.content_id === item.content_id)
        );
        linkedBrandSiteContent.push(...siteContent);
      }
    });
    
    return [...directContent, ...linkedBrandSiteContent];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="artists-page">
      {/* Hero Section */}
      <section className="editorial-container py-12 lg:py-20 border-b border-border">
        <div className="max-w-4xl">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold editorial-serif mb-6 leading-tight">
            Artists
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-2xl">
            The creators building within the ecosystem. Independent voices, shared infrastructure.
          </p>
        </div>
      </section>

      {/* Artists Grid */}
      <section className="editorial-container py-12 lg:py-16">
        {artists.length === 0 ? (
          <p className="text-center text-muted-foreground">No artists found.</p>
        ) : (
          <div className="space-y-20">
            {artists.map((artist) => {
              const artistContent = getArtistContent(artist.name, artist.artist_id);
              
              return (
                <div 
                  key={artist.artist_id} 
                  className="border-b border-border pb-16 last:border-0"
                  data-testid={`artist-${artist.slug}`}
                >
                  <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
                    {/* Artist Headshot */}
                    <div className="lg:col-span-2">
                      <div className="relative aspect-[3/4] bg-black overflow-hidden">
                        <img
                          src={convertGoogleDriveUrl(artist.image)}
                          alt={artist.name}
                          className="w-full h-full object-cover opacity-90"
                          data-testid={`artist-image-${artist.slug}`}
                        />
                      </div>
                    </div>

                    {/* Artist Info */}
                    <div className="lg:col-span-3 space-y-6">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-widest accent-red mb-2">
                          {artist.role}
                        </p>
                        <h2 
                          className="text-3xl lg:text-4xl font-bold editorial-serif mb-4"
                          data-testid={`artist-name-${artist.slug}`}
                        >
                          {artist.name}
                        </h2>
                        <p className="text-base text-foreground/80 leading-relaxed">
                          {artist.bio}
                        </p>
                      </div>
                      
                      {/* Projects */}
                      {artist.projects && artist.projects.length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 text-muted-foreground">
                            Projects
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {artist.projects.map((project, idx) => (
                              <span 
                                key={idx} 
                                className="text-xs px-3 py-1 border border-border text-muted-foreground"
                              >
                                {project}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Companies */}
                      {artist.companies && artist.companies.length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 text-muted-foreground">
                            Companies
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {artist.companies.map((company, idx) => (
                              <span 
                                key={idx} 
                                className="text-xs px-3 py-1 border border-border text-muted-foreground"
                              >
                                {company}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* External Links */}
                      <div className="flex flex-wrap gap-3">
                        {artist.website && (
                          <a 
                            href={artist.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            data-testid={`artist-website-${artist.slug}`}
                          >
                            <Button variant="outline" size="sm">
                              Visit Site
                              <ExternalLink size={14} className="ml-2" />
                            </Button>
                          </a>
                        )}
                        {artist.imdb && (
                          <a 
                            href={artist.imdb} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            data-testid={`artist-imdb-${artist.slug}`}
                          >
                            <Button variant="ghost" size="sm">
                              IMDb
                              <ExternalLink size={14} className="ml-2" />
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Related Content - Collapsible */}
                  {artistContent.length > 0 && (
                    <CollapsibleNews 
                      title={`Related Coverage & Updates (${artistContent.length})`}
                      items={artistContent}
                      convertUrl={convertGoogleDriveUrl}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Contact Section */}
      <section className="editorial-container py-16 lg:py-24 border-t border-border">
        <div className="max-w-2xl mx-auto text-center">
          <Mail size={48} className="mx-auto mb-6 text-muted-foreground" />
          <h2 className="text-3xl lg:text-4xl font-bold editorial-serif mb-4">
            Interested in the Ecosystem?
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            Cultural Revolution is building infrastructure for creator sustainability. 
            Reach out if you're building something that aligns.
          </p>
          <a href="mailto:dldagerman1@gmail.com">
            <Button size="lg" data-testid="contact-button">
              Get in Touch
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ArtistsPage;
